import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Fade,
  Slide,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowBack, ArrowForward, CheckCircle } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { validatePhoneNumber, validateZipCode } from '../../utils/inputFormatters';

// Import question type components
import TextQuestion from './questions/TextQuestion';
import EmailQuestion from './questions/EmailQuestion';
import SelectQuestion from './questions/SelectQuestion';
import SliderQuestion from './questions/SliderQuestion';
import DateQuestion from './questions/DateQuestion';
import ConditionalQuestion from './questions/ConditionalQuestion';
import TextareaQuestion from './questions/TextareaQuestion';
import MultiSelectQuestion from './questions/MultiSelectQuestion';
import PhoneQuestion from './questions/PhoneQuestion';
import StepperQuestion from './questions/StepperQuestion';

const QuestionnaireEngine = ({
  configPath = '/data/customer_engagement_questionnaire.json',
  onComplete,
  onUpdate,
  onError,
  autoSave = true,
  showProgress = true,
  allowNavigation = true,
  theme = 'default'
}) => {
  // State management
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submissionAnswers, setSubmissionAnswers] = useState({}); // Preserve answers during submission

  // Load questionnaire configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const response = await fetch(configPath);
        if (!response.ok) {
          throw new Error(`Failed to load questionnaire config: ${response.statusText}`);
        }
        const data = await response.json();
        setConfig(data);
        
        // Initialize default values for questions that have them
        const defaultAnswers = {};
        data.questions?.forEach(question => {
          if (question.options && Array.isArray(question.options)) {
            question.options.forEach(option => {
              if (typeof option === 'string' && option.startsWith('default=')) {
                const defaultValue = option.replace('default=', '');
                // Convert to number for stepper questions
                if (question.type === 'stepper') {
                  defaultAnswers[question.id] = parseInt(defaultValue, 10);
                } else {
                  defaultAnswers[question.id] = defaultValue;
                }
              }
            });
          }
        });
        
        // Set default answers if any were found
        if (Object.keys(defaultAnswers).length > 0) {
          setAnswers(defaultAnswers);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading questionnaire config:', err);
        setError(err.message);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [configPath, onError]);

  // Text interpolation helper - Enhanced with multiple variable formats
  const interpolateText = useCallback((text, answers) => {
    if (!text || typeof text !== 'string') return text;
    
    return text.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      const cleanVariable = variable.trim();
      
      // Handle special cases and transformations
      if (cleanVariable === 'name' && answers.name) {
        return answers.name.split(' ')[0]; // Use first name for personalization
      }
      
      if (cleanVariable === 'fullName' && answers.name) {
        return answers.name; // Use full name
      }
      
      if (cleanVariable === 'firstName' && answers.name) {
        return answers.name.split(' ')[0] || answers.name;
      }
      
      if (cleanVariable === 'lastName' && answers.name) {
        const nameParts = answers.name.split(' ');
        return nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      }
      
      // Handle array values (for multi-select questions)
      const value = answers[cleanVariable];
      if (Array.isArray(value)) {
        if (value.length === 0) return 'none selected';
        if (value.length === 1) return value[0];
        if (value.length === 2) return `${value[0]} and ${value[1]}`;
        return `${value.slice(0, -1).join(', ')}, and ${value[value.length - 1]}`;
      }
      
      // Handle conditional question objects
      if (value && typeof value === 'object' && value.option) {
        let result = value.option;
        if (value.followUp) {
          result += ` (${value.followUp})`;
        }
        return result;
      }
      
      // Handle date objects
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      
      // Handle numeric values with formatting
      if (typeof value === 'number') {
        return value.toLocaleString();
      }
      
      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'yes' : 'no';
      }
      
      // Return the value or the original placeholder if not found
      return value || match;
    });
  }, []);

  // Filter visible questions based on conditions
  const visibleQuestions = useMemo(() => {
    if (!config?.questions) return [];
    
    return config.questions.filter((question, index) => {
      // Always show questions without conditions
      if (!question.showIf) return true;
      
      // Handle conditional visibility
      const { field, operator, value, values } = question.showIf;
      const fieldValue = answers[field];
      
      switch (operator) {
        case 'equals':
          return fieldValue === value;
        case 'not_equals':
          return fieldValue !== value;
        case 'in':
          return values && values.includes(fieldValue);
        case 'not_in':
          return values && !values.includes(fieldValue);
        case 'contains':
          return Array.isArray(fieldValue) && fieldValue.includes(value);
        case 'not_contains':
          return Array.isArray(fieldValue) && !fieldValue.includes(value);
        case 'greater_than':
          return typeof fieldValue === 'number' && fieldValue > value;
        case 'less_than':
          return typeof fieldValue === 'number' && fieldValue < value;
        case 'exists':
          return fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
        case 'not_exists':
          return fieldValue === undefined || fieldValue === null || fieldValue === '';
        case 'any_selected':
          return Array.isArray(fieldValue) && fieldValue.length > 0;
        case 'none_selected':
          return !Array.isArray(fieldValue) || fieldValue.length === 0;
        case 'conditional_yes':
          return fieldValue && typeof fieldValue === 'object' && fieldValue.option === question.showIf.targetOption;
        default:
          return true;
      }
    });
  }, [config, answers]);

  // Current question
  const currentQuestion = visibleQuestions[currentQuestionIndex];

  // Progress calculation
  const progress = useMemo(() => {
    if (!visibleQuestions.length) return 0;
    return ((currentQuestionIndex + 1) / visibleQuestions.length) * 100;
  }, [currentQuestionIndex, visibleQuestions.length]);

  // Handle answer updates
  const handleAnswerChange = useCallback((questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    
    // Auto-save if enabled
    if (autoSave) {
      localStorage.setItem('questionnaire_answers', JSON.stringify(newAnswers));
    }
    
    // Notify parent component
    onUpdate?.(newAnswers, questionId, value);
  }, [answers, autoSave, onUpdate]);

  // Validate current question
  const validateCurrentQuestion = useCallback(() => {
    if (!currentQuestion) return { isValid: true, error: null };
    
    const value = answers[currentQuestion.id];
    
    // Check if question is required
    if (!currentQuestion.optional && (!value || (Array.isArray(value) && value.length === 0))) {
      return { isValid: false, error: 'This field is required' };
    }
    
    // Type-specific validation
    switch (currentQuestion.type) {
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return { isValid: false, error: 'Please enter a valid email address' };
        }
        break;
      case 'tel':
        if (value) {
          const phoneError = validatePhoneNumber(value);
          if (phoneError) {
            return { isValid: false, error: phoneError };
          }
        }
        break;
      default:
        break;
    }
    
    // Additional validation based on question properties
    if (currentQuestion.validation === 'zipCode' && value) {
      const zipError = validateZipCode(value);
      if (zipError) {
        return { isValid: false, error: zipError };
      }
    }
    
    return { isValid: true, error: null };
  }, [currentQuestion, answers]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    const validation = validateCurrentQuestion();
    if (!validation.isValid) {
      setError(validation.error);
      // Smooth scroll to show error
      setTimeout(() => {
        const errorElement = document.querySelector('.MuiAlert-root');
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    
    setError(null);
    
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Preserve answers before showing confirmation screen
      setSubmissionAnswers(answers);
      setShowConfirmation(true);
    }
  }, [currentQuestionIndex, visibleQuestions.length, validateCurrentQuestion, answers]);

  const handlePrevious = useCallback(() => {
    if (showConfirmation) {
      setShowConfirmation(false);
      return;
    }
    
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setError(null);
    }
  }, [currentQuestionIndex, showConfirmation]);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onComplete?.(answers);
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
      setError('Failed to submit questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, onComplete]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only handle keyboard navigation if no input is focused
      if (document.activeElement?.tagName === 'INPUT' || 
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }
      
      switch (event.key) {
        case 'ArrowRight':
        case 'Enter':
          if (!showConfirmation) {
            event.preventDefault();
            handleNext();
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          handlePrevious();
          break;
        case 'Escape':
          if (showConfirmation) {
            event.preventDefault();
            handlePrevious();
          }
          break;
        default:
          break;
      }
    };

    if (allowNavigation) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [handleNext, handlePrevious, showConfirmation, allowNavigation]);

  // Question component renderer
  const renderQuestion = useCallback((question) => {
    const commonProps = {
      question: question,
      value: answers[question.id],
      onChange: (value) => handleAnswerChange(question.id, value),
      interpolateText: interpolateText,
      answers: answers,
      error: error
    };

    switch (question.type) {
      case 'text':
        return <TextQuestion {...commonProps} />;
      case 'email':
        return <EmailQuestion {...commonProps} />;
      case 'tel':
        return <PhoneQuestion {...commonProps} />;
      case 'single-select':
        return <SelectQuestion {...commonProps} />;
      case 'multi-select':
        return <MultiSelectQuestion {...commonProps} />;
      case 'slider':
        return <SliderQuestion {...commonProps} />;
      case 'date':
        return <DateQuestion {...commonProps} />;
      case 'conditional':
        return <ConditionalQuestion {...commonProps} />;
      case 'textarea':
        return <TextareaQuestion {...commonProps} />;
      case 'stepper':
        return <StepperQuestion {...commonProps} />;
      default:
        return <TextQuestion {...commonProps} />;
    }
  }, [answers, error, handleAnswerChange, interpolateText]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading questionnaire...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error && !config) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load questionnaire: {error}
      </Alert>
    );
  }

  // Confirmation screen
  if (showConfirmation) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card sx={{ maxWidth: 600, mx: 'auto', m: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Review Your Information
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {interpolateText(config?.exitText, isSubmitting ? submissionAnswers : answers)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={isSubmitting}
                startIcon={<ArrowBack />}
              >
                Go Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                endIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />}
                sx={{ minWidth: 160 }}
              >
                {isSubmitting ? 'Submitting...' : config?.confirmationOptions?.[0] || 'Submit'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Main questionnaire interface
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Progress bar */}
      {showProgress && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Question {currentQuestionIndex + 1} of {visibleQuestions.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      )}

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, x: 50, scale: 0.98 }}
          animate={{ 
            opacity: 1, 
            x: 0, 
            scale: 1,
            transition: { 
              duration: 0.4, 
              ease: [0.25, 0.46, 0.45, 0.94],
              staggerChildren: 0.1
            }
          }}
          exit={{ 
            opacity: 0, 
            x: -50, 
            scale: 0.98,
            transition: { duration: 0.2 }
          }}
        >
          <Card 
            elevation={3}
            sx={{
              borderRadius: 3,
              boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
              border: theme => `1px solid ${theme.palette.divider}20`
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                  y: 0, 
                  opacity: 1,
                  transition: { delay: 0.1, duration: 0.3 }
                }}
              >
                {currentQuestion && renderQuestion(currentQuestion)}
              </motion.div>
              
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { duration: 0.3 }
                  }}
                >
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mt: 2,
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        fontWeight: 500
                      }
                    }}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0 && !allowNavigation}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={<ArrowForward />}
          sx={{ minWidth: 120 }}
        >
          {currentQuestionIndex === visibleQuestions.length - 1 ? 'Review' : 'Next'}
        </Button>
      </Box>

      {/* Intro text */}
      {currentQuestionIndex === 0 && config?.introText && (
        <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
            {interpolateText(config.introText, answers)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuestionnaireEngine;